import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { SendQuoteModal } from '@/components/Modals/SendQuoteModal';
import { ConvertToInvoiceModal } from '@/components/Modals/ConvertToInvoiceModal';
import { BusinessSignatureModal } from '@/components/Modals/BusinessSignatureModal';
import { ViewContractModal } from '@/components/Modals/ViewContractModal';
import { SendHistorySection } from '@/components/SendHistorySection';
import { RefundModal } from '@/components/RefundModal';
import {
  quoteApi,
  clientApi,
  taxApi,
  invoiceApi,
  templateConfigApi,
  paymentApi,
  type Quote,
  type Client,
  type Tax,
} from '@/lib/api';
import { DocumentRenderer } from '@/components/TemplateDesigner/DocumentRenderer';
import { transformLineItems, calculateSubtotal } from '@/components/TemplateDesigner/utils';
import { DEFAULT_TEMPLATE_SETTINGS, type TemplateSettings, type DocumentSignature, type SignatureLine, type DocumentContract } from '@/components/TemplateDesigner/types';
import { getQuoteStatusConfig } from '@/lib/quoteStatus';
import { formatAmount } from '@/lib/currency';
import {
  FileText,
  File,
  User,
  Calendar,
  Receipt,
  MoreVertical,
  Eye,
  Download,
  Send,
  ExternalLink,
  Check,
  Clock,
  ArrowLeft,
  Pencil,
  CreditCard,
  DollarSign,
  Archive,
  CheckCircle,
  ChevronDown,
  Landmark,
  Pen,
  FileCheck,
  RotateCcw,
  ChevronRight,
  Link,
  X,
  Bell,
  Trash2,
  MessageSquare,
  Printer,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSendBlockModal } from '@/contexts/SendBlockModalContext';

export function ViewQuote() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, user, isViewOnly } = useApp();
  const { showEmailVerificationModal, showViewOnlyBlockModal } = useSendBlockModal();
  const businessId = currentBusiness?.id || '';

  // Data state
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [error, setError] = useState('');
  const [templateSettings, setTemplateSettings] = useState<TemplateSettings>(DEFAULT_TEMPLATE_SETTINGS);

  // UI state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendMode, setSendMode] = useState<'send' | 'reminder'>('send');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureForMarkAsSent, setSignatureForMarkAsSent] = useState(false);
  const [viewContractOpen, setViewContractOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editBlockedDialogOpen, setEditBlockedDialogOpen] = useState(false);
  const [editBlockedReason, setEditBlockedReason] = useState('');
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [refundPaymentId, setRefundPaymentId] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [editingNumber, setEditingNumber] = useState(false);
  const [editNumberValue, setEditNumberValue] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  const [numberError, setNumberError] = useState('');
  const numberInputRef = useRef<HTMLInputElement>(null);

  const startEditingNumber = () => {
    if (!quote || isViewOnly) return;
    setEditNumberValue(quote.quote_number);
    setNumberError('');
    setEditingNumber(true);
    setTimeout(() => numberInputRef.current?.select(), 0);
  };

  const saveNumber = async () => {
    if (!quote || savingNumber) return;
    const trimmed = editNumberValue.trim();
    if (!trimmed || trimmed === quote.quote_number) {
      setEditingNumber(false);
      setNumberError('');
      return;
    }
    setSavingNumber(true);
    const response = await quoteApi.updateNumber(quote.id, trimmed);
    setSavingNumber(false);
    if (response.data) {
      setQuote(response.data);
      setEditingNumber(false);
      setNumberError('');
    } else {
      setNumberError(response.error || 'Failed to update');
    }
  };

  const cancelEditingNumber = () => {
    setEditingNumber(false);
  };

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Load reference data
  useEffect(() => {
    if (businessId) {
      clientApi.list(businessId).then((r) => r.data && setClients(r.data));
      taxApi.list(businessId, true).then((r) => r.data && setTaxes(r.data)); // Include archived for historical display
      templateConfigApi.get(businessId).then((r) => {
        if (r.data?.settings) {
          const savedSettings = r.data.settings as Partial<TemplateSettings>;
          setTemplateSettings({ ...DEFAULT_TEMPLATE_SETTINGS, ...savedSettings });
        }
      }).catch(() => {});
    }
  }, [businessId]);

  // Load quote
  useEffect(() => {
    if (businessId && id) {
      quoteApi.get(id).then((r) => {
        if (r.data) {
          setQuote(r.data);
          setLoading(false);
        } else if (r.error) {
          setError(r.error);
          setLoading(false);
        }
      });
    }
  }, [businessId, id, refreshKey]);

  const handleMarkAsSent = async () => {
    if (!id || !quote) return;
    // Check if business signature is required but not yet signed
    if (quote.requires_my_signature && !quote.contract_instance?.business_signed_at) {
      setSignatureForMarkAsSent(true);
      setSignatureModalOpen(true);
      return;
    }
    // Proceed with marking as sent
    const response = await quoteApi.send(id, {
      medium: 'marked_by_user',
      destination: '',
      message: '',
      cc_self: false,
    });
    if (response.sendBlocked) {
      if (response.sendBlocked.code === 'email_not_verified') {
        showEmailVerificationModal(response.sendBlocked.email || '');
      } else {
        showViewOnlyBlockModal();
      }
      return;
    }
    if (response.data) {
      setQuote(response.data.quote);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    const response = await quoteApi.approve(id);
    if (response.data) {
      setQuote(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleArchive = async () => {
    if (!id || !confirm('Archive this quote?')) return;
    const response = await quoteApi.archive(id);
    if (response.data) {
      setQuote(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Delete this quote? This cannot be undone.')) return;
    const response = await quoteApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      nav('/quotes');
    }
  };

  const handleRevoke = async () => {
    if (!id || !quote) return;
    const response = await quoteApi.revoke(id);
    if (response.data) {
      setQuote(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  // Handle signature completion
  const handleSignatureComplete = async (signatory_name: string, signature_image: string) => {
    if (!id) return;
    const response = await quoteApi.signBusiness(id, signatory_name, signature_image);
    if (response.data) {
      setQuote(response.data);
      setSignatureModalOpen(false);

      // Check if this was triggered by "Mark as Sent" flow
      if (signatureForMarkAsSent) {
        setSignatureForMarkAsSent(false);
        // Proceed with marking as sent
        const sendResponse = await quoteApi.send(id, {
          medium: 'marked_by_user',
          destination: '',
          message: '',
          cc_self: false,
        });
        if (sendResponse.sendBlocked) {
          if (sendResponse.sendBlocked.code === 'email_not_verified') {
            showEmailVerificationModal(sendResponse.sendBlocked.email || '');
          } else {
            showViewOnlyBlockModal();
          }
          return;
        }
        if (sendResponse.data) {
          setQuote(sendResponse.data.quote);
        } else if (sendResponse.error) {
          alert(sendResponse.error);
        }
      } else {
        // After signing, open the send modal
        setSendModalOpen(true);
      }
    } else if (response.error) {
      alert(response.error);
    }
  };

  // Handle send click - check if business signature is required but not yet signed
  const handleSendClick = () => {
    if (!user?.is_email_verified) {
      showEmailVerificationModal(user?.email || '');
      return;
    }
    if (quote?.requires_my_signature && !quote?.contract_instance?.business_signed_at) {
      setSignatureModalOpen(true);
      return;
    }
    setSendModalOpen(true);
  };

  // Handle remind click - opens send modal in reminder mode
  const handleRemindClick = () => {
    if (!quote) return;
    if (!user?.is_email_verified) {
      showEmailVerificationModal(user?.email || '');
      return;
    }
    setSendMode('reminder');
    setSendModalOpen(true);
  };

  // Send receipt for ALL deposit payments on this quote
  const handleSendReceipt = async () => {
    if (!quote || !quote.payment_allocations?.length) return;

    // Get all unique payment IDs (excluding credit memos)
    const paymentIds = [...new Set(
      quote.payment_allocations
        .filter(a => !a.is_credit_memo && a.status === 'completed')
        .map(a => a.payment_id)
    )];

    if (paymentIds.length === 0) {
      toast.error('No completed payments found to send receipts for');
      return;
    }

    setSendingReceipt(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const paymentId of paymentIds) {
        const response = await paymentApi.sendReceipt(paymentId);
        if (response.error) {
          errorCount++;
        } else {
          successCount++;
        }
      }

      if (errorCount === 0) {
        toast.success(paymentIds.length === 1 ? 'Receipt sent successfully' : `${successCount} receipts sent successfully`);
      } else if (successCount === 0) {
        toast.error('Failed to send receipts');
      } else {
        toast.warning(`${successCount} receipts sent, ${errorCount} failed`);
      }
    } catch {
      toast.error('Failed to send receipts');
    } finally {
      setSendingReceipt(false);
    }
  };

  // Download receipt PDF for ALL deposit payments on this quote
  const handleDownloadReceipt = async () => {
    if (!quote || !quote.payment_allocations?.length) return;

    // Get all unique payment IDs (excluding credit memos)
    const paymentIds = [...new Set(
      quote.payment_allocations
        .filter(a => !a.is_credit_memo && a.status === 'completed')
        .map(a => a.payment_id)
    )];

    if (paymentIds.length === 0) {
      toast.error('No completed payments found to download receipts for');
      return;
    }

    setDownloadingReceipt(true);
    try {
      const token = localStorage.getItem('accessToken');

      for (const paymentId of paymentIds) {
        const url = paymentApi.getReceiptDownloadUrl(paymentId);
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          toast.error(`Failed to download receipt for payment ${paymentId}`);
          continue;
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `receipt-${paymentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      }

      if (paymentIds.length > 1) {
        toast.success(`${paymentIds.length} receipts downloaded`);
      }
    } catch {
      toast.error('Failed to download receipts');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  // Delete a deposit payment
  const handleDeletePayment = async () => {
    if (!deletePaymentId || !id) return;
    setDeletingPayment(true);
    try {
      const response = await paymentApi.delete(deletePaymentId);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Payment deleted');
        // Refresh quote data
        const refreshed = await quoteApi.get(id);
        if (refreshed.data) {
          setQuote(refreshed.data);
        }
      }
    } catch {
      toast.error('Failed to delete payment');
    } finally {
      setDeletingPayment(false);
      setDeletePaymentId(null);
    }
  };

  const handleEditClick = () => {
    if (!quote) return;
    if (quote.status === 'approved') {
      setEditBlockedReason('This quote cannot be edited because the deposit has been collected.');
      setEditBlockedDialogOpen(true);
    } else if (quote.status === 'converted') {
      setEditBlockedReason('This quote cannot be edited because it has been converted to an invoice.');
      setEditBlockedDialogOpen(true);
    } else {
      nav(`/quotes/${quote.id}/edit`);
    }
  };

  const handleConvert = async (options: { sendEmail: boolean; dueDate: string }) => {
    if (!id) return;
    const response = await quoteApi.convertToInvoice(id, options.dueDate);
    if (response.data) {
      const invoiceId = response.data.invoice.id;

      if (options.sendEmail && selectedClient?.emails?.length) {
        await invoiceApi.send(invoiceId, {
          medium: 'email',
          destination: selectedClient.emails.join(','),
          message: '',
          cc_self: false,
        });
      }

      nav(`/invoices/${invoiceId}`);
    } else if (response.error) {
      alert(response.error);
    }
  };

  // Calculations
  const calculateTaxes = () => {
    if (!quote) return {};
    const taxTotals: { [taxId: string]: { name: string; rate: number; amount: number } } = {};

    // Calculate discount ratio to apply proportionally to taxable amounts
    const totalDiscount = parseFloat(quote.total_discount || '0');

    // Calculate taxable base (sum of computed_amounts for items with taxes)
    let taxableBase = 0;
    quote.line_items.forEach(([, items]) => {
      items.forEach((item) => {
        if (item.tax_ids && item.tax_ids.length > 0) {
          taxableBase += item.computed_amount ?? item.amount;
        }
      });
    });

    // Discount ratio reduces the taxable base proportionally
    const discountRatio = taxableBase > 0 && totalDiscount > 0 ? Math.min(totalDiscount / taxableBase, 1) : 0;

    quote.line_items.forEach(([, items]) => {
      items.forEach((item) => {
        (item.tax_ids || []).forEach((taxId) => {
          const tax = taxes.find((t) => t.id === taxId);
          if (tax) {
            const taxRate = parseFloat(tax.rate) / 100;
            // Use computed_amount (includes markup) and apply discount ratio
            const itemAmount = item.computed_amount ?? item.amount;
            const discountedAmount = itemAmount * (1 - discountRatio);
            const taxAmount = discountedAmount * taxRate;
            if (!taxTotals[taxId]) {
              taxTotals[taxId] = { name: tax.name, rate: parseFloat(tax.rate), amount: 0 };
            }
            taxTotals[taxId].amount += taxAmount;
          }
        });
      });
    });
    return taxTotals;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCopyLink = () => {
    if (!quote) return;
    const url = `${window.location.origin}/quote/${quote.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const canShareLink = quote && ['awaiting_response', 'approved', 'changes_requested'].includes(quote.status);


  if (loading) return <div className="fixed inset-0 z-50 bg-background" />;

  if (!quote || error) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
          <div className="h-6 w-px bg-border" />
          <span className="font-medium text-sm">{currentBusiness?.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Quote not found'}</p>
            <Button variant="outline" onClick={() => nav('/quotes')}>
              Back to Quotes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const selectedClient = clients.find((c) => c.id === quote.client);
  const taxTotals = calculateTaxes();
  const subtotal = parseFloat(quote.subtotal);
  const totalMarkup = parseFloat(quote.total_markup);
  const totalDiscount = parseFloat(quote.total_discount);
  const total = parseFloat(quote.total_amount);
  const depositRequired = parseFloat(quote.deposit.toString());
  const depositCollected = parseFloat(quote.amount_paid);
  const depositBalance = parseFloat(quote.balance_due);
  const statusConfig = getQuoteStatusConfig(quote.status, `Quote #${quote.quote_number}`);

  const canCollectDeposit = depositRequired > 0 &&
    depositBalance > 0 &&
    quote.status === 'awaiting_response';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => nav('/quotes')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Quotes
              </button>
              {selectedClient && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <button
                    onClick={() => nav(`/clients/${selectedClient.id}`)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {selectedClient.billing_name}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              <div className="flex-1" />
              <Button
                variant="outline"
                size="sm"
                disabled={generatingPdf}
                onClick={async () => {
                  setGeneratingPdf(true);
                  const res = await quoteApi.generatePdf(quote.id);
                  setGeneratingPdf(false);
                  if (res.error || !res.data?.pdf_url) {
                    toast.error('Failed to generate PDF');
                    return;
                  }
                  setQuote({ ...quote, pdf_url: res.data.pdf_url });
                  const w = window.open(`${res.data.pdf_url}?t=${Date.now()}`, '_blank');
                  if (w) {
                    w.addEventListener('load', () => w.print());
                  }
                }}
              >
                {generatingPdf ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Printer className="h-4 w-4 mr-1" />
                )}
                {generatingPdf ? 'Generating...' : 'Print / Download'}
              </Button>
              {canShareLink && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  <Link className="h-4 w-4 mr-1" />
                  Copy Link
                </Button>
              )}
              {!isViewOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="max-w-3xl mx-auto space-y-6"
              >
                {/* Title with Status */}
                <div className="flex items-center gap-3 flex-wrap">
                  {editingNumber ? (
                    <div className="flex flex-col gap-1 py-0.5">
                      <div className="flex items-center gap-1.5">
                        <Input
                          ref={numberInputRef}
                          value={editNumberValue}
                          onChange={(e) => { setEditNumberValue(e.target.value); setNumberError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveNumber(); if (e.key === 'Escape') cancelEditingNumber(); }}
                          onBlur={saveNumber}
                          className={`h-9 text-lg w-64 ${numberError ? 'border-destructive' : ''}`}
                          disabled={savingNumber}
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={cancelEditingNumber}
                          className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                      {numberError && <p className="text-xs text-destructive">{numberError}</p>}
                    </div>
                  ) : (
                    <h1
                      className="text-2xl flex items-center gap-2 cursor-pointer hover:text-primary/80 transition-colors group"
                      onClick={startEditingNumber}
                      title="Click to edit"
                    >
                      {quote.quote_number}
                      <FileText className="h-5 w-5 text-yellow-500" />
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h1>
                  )}
                  <Badge variant="outline" className={statusConfig.textColorClass}>{statusConfig.label}</Badge>
                  {quote.status === 'converted' && quote.converted_invoice && (
                    <button
                      onClick={() => nav(`/invoices/${quote.converted_invoice!.id}`)}
                      className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      <FileText className="h-4 w-4" />
                      View Invoice #{quote.converted_invoice.invoice_number}
                    </button>
                  )}
                </div>

                {/* Changes Requested Banner */}
                {quote.status === 'changes_requested' && quote.change_requests && quote.change_requests.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 }}
                    className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1 min-w-0">
                        <p className="font-medium text-orange-800 dark:text-orange-200">Client Requested Changes</p>
                        <p className="text-sm text-orange-700 dark:text-orange-300 whitespace-pre-wrap break-words">
                          {quote.change_requests[0].text}
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-400">
                          {new Date(quote.change_requests[0].created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Client Section */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="space-y-2"
                >
                  <Label className="text-muted-foreground">Client</Label>
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p>{selectedClient?.billing_name || quote.client_name || 'No client'}</p>
                        {(selectedClient?.company_name || quote.client_company) && (
                          <p className="text-sm text-muted-foreground">
                            {selectedClient?.company_name || quote.client_company}
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedClient && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`/clients/${selectedClient.id}`)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    )}
                  </div>
                </motion.div>

                {/* Quote Date & Expiration */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Quote Date</Label>
                    <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(quote.quote_date)}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Expiration Date</Label>
                    <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(quote.due_date)}</span>
                    </div>
                  </div>
                  {quote.custom_field_values && Object.keys(quote.custom_field_values).length > 0 && templateSettings.headerFields
                    .filter(f => f.enabled && f.isCustom && f.documentTypes.includes('quote') && f.id in quote.custom_field_values)
                    .map(field => (
                      <div key={field.id} className="space-y-2">
                        <Label className="text-muted-foreground">{field.name}</Label>
                        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                          <span>{quote.custom_field_values[field.id] || '—'}</span>
                        </div>
                      </div>
                    ))}
                </motion.div>

                {/* Line Items */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Line Items</Label>

                  {quote.line_items.map(([sectionName, sectionItems], sectionIndex) => (
                    <div key={sectionIndex} className="border rounded-lg overflow-hidden">
                      {(quote.line_items.length > 1 || sectionName) && sectionName && (
                        <div className="bg-yellow-500/5 border-b px-4 py-2.5 font-medium">
                          {sectionName}
                        </div>
                      )}
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr className="text-xs text-muted-foreground">
                            <th className="text-left px-4 py-2">Item</th>
                            <th className="text-left px-3 py-2 w-16">Type</th>
                            <th className="text-right px-3 py-2 w-20">Qty</th>
                            <th className="text-right px-3 py-2 w-24">Rate</th>
                            <th className="text-right px-4 py-2 w-28">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionItems.map((item, itemIndex) => (
                            <tr key={item.id || `${sectionIndex}-${itemIndex}`} className="border-t">
                              <td className="px-4 py-3">
                                <div className="font-medium">{item.name || '-'}</div>
                                {item.description && (
                                  <div className="text-sm text-muted-foreground mt-0.5">{item.description}</div>
                                )}
                                {item.tax_ids.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {item.tax_ids.map((taxId) => {
                                      const tax = taxes.find((t) => t.id === taxId);
                                      if (!tax) return null;
                                      return (
                                        <span key={taxId} className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                          {tax.name} {tax.rate}%
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-muted-foreground">
                                {item.item_type === 'labor' ? 'Labor' : item.item_type === 'other' ? 'Other' : 'Parts'}
                              </td>
                              <td className="px-3 py-3 text-right text-muted-foreground">{item.quantity}</td>
                              <td className="px-3 py-3 text-right text-muted-foreground">{formatAmount(item.computed_rate ?? item.rate)}</td>
                              <td className="px-4 py-3 text-right font-medium">{formatAmount(item.computed_amount ?? item.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </motion.div>

                {/* Totals */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="border-t pt-4 space-y-2"
                >
                  {/* 1. Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatAmount(subtotal)}</span>
                  </div>

                  {/* 2. Markup */}
                  {totalMarkup > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Markup</span>
                      <span>+{formatAmount(totalMarkup)}</span>
                    </div>
                  )}

                  {/* 3. Discount */}
                  {totalDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-green-600">-{formatAmount(totalDiscount)}</span>
                    </div>
                  )}

                  {/* Taxes */}
                  {Object.keys(taxTotals).length > 0 ? (
                    Object.entries(taxTotals).map(([taxId, tax]) => (
                      <div key={taxId} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{tax.name} ({tax.rate}%)</span>
                        <span>{formatAmount(tax.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Taxes</span>
                      <span>{formatAmount(0)}</span>
                    </div>
                  )}

                  {/* 6. Total */}
                  <div className="flex justify-between text-lg pt-2 border-t">
                    <span>Total</span>
                    <span>{formatAmount(total)}</span>
                  </div>

                  {/* 7. Deposit Tracking */}
                  {depositRequired > 0 && (
                    <>
                      <div className="flex justify-between text-sm pt-2">
                        <span className="text-muted-foreground">Deposit Required</span>
                        <span>{formatAmount(depositRequired)}</span>
                      </div>
                      {depositCollected > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Deposit Collected</span>
                            <span className="text-green-600">-{formatAmount(depositCollected)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-semibold">
                            <span>Deposit Balance</span>
                            <span className={depositBalance > 0 ? 'text-amber-600' : 'text-green-600'}>
                              {formatAmount(depositBalance)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </motion.div>

                {/* Deposits Received */}
                {quote.payment_allocations && quote.payment_allocations.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-medium">Deposits Received</Label>
                    </div>
                    <div className="border rounded-lg divide-y">
                      {quote.payment_allocations.map((alloc) => (
                        <div key={alloc.id} className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-3">
                            {/* Icon based on payment type and status */}
                            {alloc.is_refund ? (
                              <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                                <RotateCcw className="h-4 w-4 text-red-600" />
                              </div>
                            ) : alloc.status === 'completed' ? (
                              <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                                <Check className="h-4 w-4 text-green-600" />
                              </div>
                            ) : alloc.status === 'pending' ? (
                              <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-amber-600" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                                <X className="h-4 w-4 text-red-600" />
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                {alloc.is_refund ? (
                                  <span className="text-red-600">Refund</span>
                                ) : alloc.is_credit_memo ? (
                                  <span className="text-amber-600">Credit Memo #{alloc.credit_memo_number}</span>
                                ) : (
                                  <span className="capitalize">{alloc.payment_method.replace(/_/g, ' ')}</span>
                                )}
                                {alloc.status === 'pending' && !alloc.is_refund && (
                                  <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">Processing</span>
                                )}
                                {alloc.status === 'pending' && alloc.is_refund && (
                                  <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">Pending</span>
                                )}
                                {alloc.status === 'failed' && (
                                  <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded">Failed</span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(alloc.payment_date).toLocaleDateString()}
                                {alloc.payment_reference && ` • ${alloc.payment_reference}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`font-medium ${
                              alloc.is_refund ? 'text-red-600' :
                              alloc.status === 'completed' ? 'text-green-600' :
                              alloc.status === 'pending' ? 'text-amber-600' :
                              'text-red-600 line-through'
                            }`}>{alloc.is_refund ? '-' : ''}{formatAmount(Math.abs(parseFloat(alloc.amount)))}</span>
                            {/* Show dropdown for non-credit-memo payments */}
                            {!alloc.is_credit_memo && !alloc.is_refund && !isViewOnly && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {alloc.is_refundable && (
                                    <DropdownMenuItem onClick={() => setRefundPaymentId(alloc.payment_id)}>
                                      <RotateCcw className="h-4 w-4 mr-2" />
                                      Refund
                                    </DropdownMenuItem>
                                  )}
                                  {!alloc.is_stripe_payment && (
                                    <DropdownMenuItem
                                      onClick={() => setDeletePaymentId(alloc.payment_id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Send History */}
                {quote.send_records && quote.send_records.length > 0 && (
                  <SendHistorySection records={quote.send_records} delay={0.4} />
                )}
              </motion.div>
            </div>
          </motion.div>

          {/* Right Sidebar */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
          >
            {/* Deposit Balance Header */}
            <div className="px-5 py-4 border-b">
              {depositRequired > 0 ? (
                <>
                  <div className="text-sm text-muted-foreground">Deposit Balance</div>
                  <div className={`text-2xl font-semibold ${depositBalance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {formatAmount(depositBalance)}
                  </div>
                  {/* Pending Payment Notice */}
                  {parseFloat(quote.pending_payment_amount) > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-sm text-amber-600">
                      <Clock className="h-4 w-4" />
                      <span>{formatAmount(quote.pending_payment_amount)} processing</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">Quote Total</div>
                  <div className="text-2xl font-semibold">
                    {formatAmount(total)}
                  </div>
                </>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 pt-4 space-y-5">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Details</h2>

              {/* Payment Methods */}
              {depositRequired > 0 && (
                <div className="space-y-3">
                  <Label className="text-muted-foreground">Payment Methods (for deposit)</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Card</span>
                      </div>
                      <span className={`text-sm ${quote.stripe_card_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {quote.stripe_card_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Landmark className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">ACH/Bank</span>
                      </div>
                      <span className={`text-sm ${quote.stripe_ach_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {quote.stripe_ach_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Contract Signatures */}
              {(
                // Case 1: Has instance with signature requirements
                (quote.contract_instance && (
                  quote.contract_instance.business_signature_required ||
                  quote.contract_instance.counterparty_signature_required
                )) ||
                // Case 2: Template-only (no instance yet)
                (!quote.contract_instance && quote.contract_template && (
                  quote.requires_my_signature || quote.requires_client_signature
                ))
              ) ? (
                <div className="space-y-3">
                  <Label className="text-muted-foreground">Contract Signatures</Label>
                  <div className="space-y-2">
                    {(quote.contract_instance?.business_signature_required || (!quote.contract_instance && quote.requires_my_signature)) && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Your Signature</span>
                        {quote.contract_instance?.business_signed_at ? (
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <Check className="h-3 w-3" /> Signed
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSignatureModalOpen(true)}
                          >
                            <Pen className="h-3 w-3 mr-1" />
                            Sign
                          </Button>
                        )}
                      </div>
                    )}
                    {(quote.contract_instance?.counterparty_signature_required || (!quote.contract_instance && quote.requires_client_signature)) && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Client Signature</span>
                        {quote.contract_instance?.counterparty_signed_at ? (
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <Check className="h-3 w-3" /> Signed
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Pending</span>
                        )}
                      </div>
                    )}
                  </div>
                  {quote.contract_instance && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setViewContractOpen(true)}
                    >
                      <FileCheck className="h-4 w-4 mr-1" />
                      View Contract
                    </Button>
                  )}
                </div>
              ) : null}

              {/* Attachments */}
              {quote.attachments && quote.attachments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Attachments</Label>
                  <div className="space-y-2">
                    {quote.attachments.map((att, index) => (
                      <a
                        key={index}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded-lg bg-background hover:bg-muted/50 transition-colors"
                      >
                        <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate flex-1">{att.filename}</span>
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Location */}
              {quote.job_location && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Job Location</Label>
                  <div className="text-sm bg-background rounded-lg p-3 border whitespace-pre-wrap">
                    {quote.job_location}
                  </div>
                </div>
              )}

              {/* Note for Client */}
              {quote.note_for_client && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Note for Client</Label>
                  <div className="text-sm bg-background rounded-lg p-3 border whitespace-pre-wrap">
                    {quote.note_for_client}
                  </div>
                </div>
              )}

              {/* Internal Notes */}
              {quote.internal_notes && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Internal Notes</Label>
                  <div className="text-sm bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 border border-amber-200 dark:border-amber-900 whitespace-pre-wrap">
                    {quote.internal_notes}
                  </div>
                </div>
              )}

              {/* Client Change Requests - only show when not approved/converted */}
              {quote.status !== 'approved' && quote.status !== 'converted' && quote.change_requests && quote.change_requests.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Client Feedback</Label>
                  <div className="space-y-2">
                    {quote.change_requests.map((cr) => (
                      <div
                        key={cr.id}
                        className="text-sm bg-orange-50 dark:bg-orange-950/30 rounded-lg p-3 border border-orange-200 dark:border-orange-900"
                      >
                        <p className="whitespace-pre-wrap">{cr.text}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(cr.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Fixed action buttons at bottom */}
            <div className="p-4 border-t bg-background space-y-3">
              {/* Draft status buttons */}
              {quote.status === 'draft' && (
                <>
                  {!isViewOnly && (
                    <>
                      <Button
                        type="button"
                        className="w-full"
                        onClick={handleSendClick}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Send
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full">
                            <MoreVertical className="h-4 w-4 mr-1" />
                            More Actions
                            <ChevronDown className="h-4 w-4 ml-auto" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={handleMarkAsSent}>
                            <Send className="h-4 w-4 mr-2" />
                            Mark as Sent
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {quote.pdf_url && (
                            <DropdownMenuItem asChild>
                              <a href={quote.pdf_url} target="_blank" rel="noopener noreferrer" download>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setPreviewMode(true)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview as Client
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleArchive}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          {parseFloat(quote.amount_paid) === 0 && (
                            <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </>
              )}

              {/* Awaiting Response status buttons */}
              {quote.status === 'awaiting_response' && (
                <>
                  {!isViewOnly && (
                    <>
                      {canCollectDeposit && (
                        <Button
                          type="button"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => nav(`/payments/new?client_id=${quote.client}&quote_id=${quote.id}`)}
                          disabled={!quote.client}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Collect Deposit
                        </Button>
                      )}
                      <Button
                        type="button"
                        className="w-full"
                        onClick={handleRemindClick}
                      >
                        <Bell className="h-4 w-4 mr-1" />
                        Send Reminder
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full">
                            <MoreVertical className="h-4 w-4 mr-1" />
                            More Actions
                            <ChevronDown className="h-4 w-4 ml-auto" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={handleApprove}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark as Approved
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleArchive}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          {parseFloat(quote.amount_paid) === 0 && (
                            <>
                              <DropdownMenuItem onClick={handleRevoke}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Revoke to Draft
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          {quote.pdf_url && (
                            <DropdownMenuItem asChild>
                              <a href={quote.pdf_url} target="_blank" rel="noopener noreferrer" download>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setPreviewMode(true)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview as Client
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </>
              )}

              {/* Approved status buttons */}
              {quote.status === 'approved' && (
                <>
                  {!isViewOnly && (
                    <>
                      <Button
                        type="button"
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => setConvertModalOpen(true)}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Convert to Invoice
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full">
                            <MoreVertical className="h-4 w-4 mr-1" />
                            More Actions
                            <ChevronDown className="h-4 w-4 ml-auto" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {quote.payment_allocations && quote.payment_allocations.length > 0 && (
                            <>
                              <DropdownMenuItem onClick={handleDownloadReceipt} disabled={downloadingReceipt}>
                                <Download className="h-4 w-4 mr-2" />
                                {downloadingReceipt ? 'Downloading...' : 'Download Receipt'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={handleSendReceipt} disabled={sendingReceipt}>
                                <Send className="h-4 w-4 mr-2" />
                                {sendingReceipt ? 'Sending...' : 'Send Receipt'}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={handleArchive}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          {parseFloat(quote.amount_paid) === 0 && (
                            <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {quote.pdf_url && (
                            <DropdownMenuItem asChild>
                              <a href={quote.pdf_url} target="_blank" rel="noopener noreferrer" download>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </a>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </>
              )}

              {/* Converted status - show link to invoice */}
              {quote.status === 'converted' && quote.converted_invoice && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => nav(`/invoices/${quote.converted_invoice!.id}`)}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  View Invoice
                </Button>
              )}

            </div>
          </motion.div>
        </div>
      </div>

      {/* Send Quote Modal (also used for reminders) */}
      <SendQuoteModal
        open={sendModalOpen}
        onOpenChange={(open) => {
          setSendModalOpen(open);
          if (!open) setSendMode('send'); // Reset mode when closing
        }}
        quote={quote}
        client={selectedClient || null}
        business={currentBusiness}
        mode={sendMode}
        onSend={async (data) => {
          if (!id) return;
          // Convert 'sms' to 'text' for API
          const apiMedium = data.medium === 'sms' ? 'text' : 'email';
          if (sendMode === 'reminder') {
            // Use remind API for reminders
            const response = await quoteApi.remind(id, {
              medium: apiMedium,
              destination: data.to.join(','),
              subject: data.medium === 'email' ? data.subject : undefined,
              message: data.message,
            });
            if (response.data) {
              setRefreshKey((k) => k + 1);
            }
            toast.success('Reminder sent');
          } else {
            // Use send API for initial send
            const response = await quoteApi.send(id, {
              medium: apiMedium,
              destination: data.to.join(','),
              subject: data.medium === 'email' ? data.subject : undefined,
              message: data.message,
              cc_self: data.cc_self,
            });
            if (response.sendBlocked) {
              setSendModalOpen(false);
              if (response.sendBlocked.code === 'email_not_verified') {
                showEmailVerificationModal(response.sendBlocked.email || '');
              } else {
                showViewOnlyBlockModal();
              }
              return;
            }
            if (response.data) {
              setQuote(response.data.quote);
            }
          }
          setSendModalOpen(false);
        }}
      />

      {/* Convert to Invoice Modal */}
      <ConvertToInvoiceModal
        open={convertModalOpen}
        onOpenChange={setConvertModalOpen}
        quote={quote}
        client={selectedClient || null}
        onConvert={handleConvert}
      />

      {/* Business Signature Modal */}
      <BusinessSignatureModal
        open={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setSignatureForMarkAsSent(false);
        }}
        onComplete={handleSignatureComplete}
        businessName={currentBusiness?.name || ''}
        defaultName={user ? `${user.first_name} ${user.last_name}`.trim() : ''}
      />

      {/* View Contract Modal */}
      {quote.contract_instance && (
        <ViewContractModal
          open={viewContractOpen}
          onClose={() => setViewContractOpen(false)}
          contractInstance={quote.contract_instance}
        />
      )}

      {/* Edit Blocked Dialog */}
      <AlertDialog open={editBlockedDialogOpen} onOpenChange={setEditBlockedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Edit Quote</AlertDialogTitle>
            <AlertDialogDescription>{editBlockedReason}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setEditBlockedDialogOpen(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Payment Confirmation Dialog */}
      <AlertDialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this deposit payment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeletePaymentId(null)} disabled={deletingPayment}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePayment} disabled={deletingPayment}>
              {deletingPayment ? 'Deleting...' : 'Delete'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refund Modal */}
      {refundPaymentId && (
        <RefundModalWrapper
          paymentId={refundPaymentId}
          open={!!refundPaymentId}
          onOpenChange={(open) => !open && setRefundPaymentId(null)}
          onRefundSuccess={() => {
            setRefundPaymentId(null);
            setRefreshKey((k) => k + 1);
            toast.success('Refund processed successfully');
          }}
        />
      )}

      {/* Client Preview Mode */}
      {previewMode && quote && currentBusiness && (
        <div className="fixed inset-0 z-[60] bg-gray-100 overflow-auto">
          <Button
            variant="outline"
            size="icon"
            className="fixed top-4 right-4 z-50 bg-white shadow-lg hover:bg-gray-100 rounded-full"
            onClick={() => setPreviewMode(false)}
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="min-h-screen py-8 px-4 flex justify-center">
            <div className="w-full max-w-[816px]">
              <DocumentRenderer
                data={(() => {
                  // Build signatures and signature_lines from contract_instance
                  const signatures: DocumentSignature[] = [];
                  const signature_lines: SignatureLine[] = [];
                  let contract: DocumentContract | undefined;

                  const ci = quote.contract_instance;
                  if (ci) {
                    // Build contract object
                    contract = {
                      content: ci.cached_rendered_content,
                      requires_business_signature: ci.business_signature_required,
                      requires_client_signature: ci.counterparty_signature_required,
                    };

                    // Add signature lines for required signatures (defines where signatures appear)
                    if (ci.business_signature_required) {
                      signature_lines.push({
                        role: 'business',
                        label: 'Authorized Signature',
                      });
                    }
                    if (ci.counterparty_signature_required) {
                      signature_lines.push({
                        role: 'client',
                        label: 'Client Signature',
                      });
                    }

                    // Add captured signatures (fills in the data for signed lines)
                    if (ci.business_signed_at && ci.business_signatory) {
                      signatures.push({
                        role: 'business',
                        label: 'Authorized Signature',
                        name: ci.business_signatory,
                        image: ci.business_signature_image || undefined,
                        signed_at: ci.business_signed_at,
                      });
                    }
                    if (ci.counterparty_signed_at && ci.counterparty_signatory) {
                      signatures.push({
                        role: 'client',
                        label: 'Client Signature',
                        name: ci.counterparty_signatory,
                        image: ci.counterparty_signature_image || undefined,
                        signed_at: ci.counterparty_signed_at,
                      });
                    }
                  }

                  // Use computed values for preview (markup baked into line items)
                  const computedSubtotal = calculateSubtotal(quote.line_items);

                  return {
                    documentType: 'quote' as const,
                    id: quote.id,
                    quote_number: quote.quote_number,
                    client_name: quote.client_name,
                    client_company: quote.client_company,
                    client_address: quote.client_address,
                    quote_date: quote.quote_date || '',
                    due_date: quote.due_date || '',
                    status: quote.status,
                    line_items: transformLineItems(quote.line_items, taxes),
                    note_for_client: quote.note_for_client,
                    subtotal: computedSubtotal,
                    total_tax: parseFloat(quote.total_tax),  // Use backend calculation (accounts for discount)
                    total_markup: 0,  // Markup is baked into line item rates, don't show separately
                    total_discount: parseFloat(quote.total_discount),
                    total_amount: parseFloat(quote.total_amount),
                    // Shipping data
                    ship_enabled: quote.ship_enabled,
                    ship_fee: quote.ship_fee,
                    ship_date: quote.ship_date,
                    ship_via: quote.ship_via,
                    ship_tracking_number: quote.ship_tracking_number,
                    custom_field_values: quote.custom_field_values,
                    ...(signatures.length > 0 ? { signatures } : {}),
                    ...(signature_lines.length > 0 ? { signature_lines } : {}),
                    ...(contract ? { contract } : {}),
                  };
                })()}
                settings={templateSettings}
                business={{
                  name: currentBusiness.name,
                  address: currentBusiness.address || '',
                  payment_instructions: currentBusiness.payment_instructions || '',
                  currency_code: currentBusiness.currency_code,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Helper component to load payment data for refund modal
function RefundModalWrapper({
  paymentId,
  open,
  onOpenChange,
  onRefundSuccess,
}: {
  paymentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefundSuccess: () => void;
}) {
  const [payment, setPayment] = useState<import('@/lib/api').Payment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && paymentId) {
      setLoading(true);
      paymentApi.get(paymentId).then((response) => {
        if (response.data) {
          setPayment(response.data);
        }
        setLoading(false);
      });
    }
  }, [open, paymentId]);

  if (loading || !payment) {
    return null;
  }

  return (
    <RefundModal
      open={open}
      onOpenChange={onOpenChange}
      payment={payment}
      onRefundSuccess={onRefundSuccess}
    />
  );
}
